import {
  countFacets,
  filterShelf,
  orderShelf,
  type ShelfLabels,
  splitShelf,
} from "@mfa/v5-contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppContext } from "../../app/AppContext";
import { useBillingAccount } from "../../core/billing-hooks";
import { InlineApiError } from "../../shared/Shared";
import { useCatalogSnapshot } from "./catalog-hooks";
import { categoryLabelMap, keywordLabelMap } from "./category-style";
import { catalogToGalleryEntry } from "./gallery/catalog-entry";
import { TemplateCard } from "./gallery/TemplateCard";
import { TemplateFilters } from "./gallery/TemplateFilters";
import {
  canSortByNewest,
  capabilitiesOf,
  categoryOptions,
  clearFilters,
  collectionsOf,
  hasActiveFilters,
  readFilters,
  siteShelf,
  type TemplateFilters as TemplateFilterState,
  vocabularyOptions,
  writeFilters,
} from "./gallery/template-filters";
import styles from "./TemplatesPage.module.css";
import { templateDetailPath } from "./use-template";

/**
 * The template gallery, ported from V1: header + plan badge, the guided-builder banner, the
 * filter bar, the card grid, and the dark Pro upsell band. Signed out or in, both sites render
 * this one page (theme-driven via the --brand-* layer) over the same catalog; signed out it
 * drops the plan badge, and a card opens the standalone detail instead of the modal.
 *
 * The shelf is the catalog snapshot: one cacheable GET for every live template, the
 * vocabulary and the site's shelf, filtered, counted and ordered here by the contracts shelf
 * module (V6 plan rows H.6 and H.24; workflows/V5-Template-Method-Review.md L1, L2, L4,
 * decision D5), so the server and any front-end list the same shelf. The filter bar's state
 * lives in the URL; the search box filters at once and writes the URL ~150 ms after the last
 * keystroke. No library is shown: libraries are storage. The site's shelf pins one blank to
 * lead the unfiltered shelf; then the site's own templates come before the other site's
 * (decision D2), each newer-then-Original by title (an Original carries the `original`
 * keyword).
 */

const ink = "var(--brand-ink)";
const card = "var(--brand-card)";

/** How long the search box waits after the last keystroke before writing the URL. */
export const SEARCH_URL_DELAY_MS = 150;

export function TemplatesPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, experience, viewer } = useAppContext();
  const anonymous = !viewer;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilters = useMemo(() => readFilters(searchParams), [searchParams]);
  // The search box's text runs ahead of the URL by the debounce; the URL's own q wins
  // whenever it changes (a link, a reload, Clear filters).
  const [draftQ, setDraftQ] = useState(urlFilters.q);
  useEffect(() => setDraftQ(urlFilters.q), [urlFilters.q]);
  const filters = useMemo(() => ({ ...urlFilters, q: draftQ }), [urlFilters, draftQ]);
  const pendingSearch = useRef<{ timer: ReturnType<typeof setTimeout>; q: string } | null>(null);
  const writeSearch = useCallback(
    (q: string) =>
      // Replace, not push: typing a search should not stack history entries; only q is
      // rewritten, so a chip toggled meanwhile is kept.
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (q === "") next.delete("q");
          else next.set("q", q);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const flushSearch = useCallback(() => {
    const pending = pendingSearch.current;
    if (!pending) return;
    clearTimeout(pending.timer);
    pendingSearch.current = null;
    writeSearch(pending.q);
  }, [writeSearch]);
  useEffect(
    () => () => {
      if (pendingSearch.current) clearTimeout(pendingSearch.current.timer);
    },
    [],
  );
  function onSearch(q: string) {
    setDraftQ(q);
    if (pendingSearch.current) clearTimeout(pendingSearch.current.timer);
    pendingSearch.current = {
      q,
      timer: setTimeout(() => {
        pendingSearch.current = null;
        writeSearch(q);
      }, SEARCH_URL_DELAY_MS),
    };
  }
  function setFilters(next: TemplateFilterState) {
    // A control writes at once, with the search text as typed so far.
    if (pendingSearch.current) clearTimeout(pendingSearch.current.timer);
    pendingSearch.current = null;
    setDraftQ(next.q);
    setSearchParams((current) => writeFilters(current, next), { replace: true });
  }

  const snapshotQuery = useCatalogSnapshot(locale);
  const billingQuery = useBillingAccount({ enabled: !anonymous });
  const snapshot = snapshotQuery.data;
  const all = useMemo(() => (snapshot?.templates ?? []).map(catalogToGalleryEntry), [snapshot]);
  const vocabulary = snapshot?.vocabulary;
  const labels = useMemo(() => {
    const facets = vocabulary ? { vocabulary } : undefined;
    return {
      categories: categoryLabelMap(facets),
      keywords: keywordLabelMap(facets),
      industries: new Map((vocabulary?.industries ?? []).map((term) => [term.key, term.label])),
      categoryOrder: (vocabulary?.categories ?? []).map((term) => term.key),
    };
  }, [vocabulary]);
  // The site's shelf (content/shelves/<site>.yaml, in the snapshot) says what leads: its one
  // pinned blank, the explicit start-from-scratch choice now that the Builder never silently
  // creates a blank project. Every other blank stays out of the gallery (its URL still
  // opens), and no blank is counted by a filter.
  const shelfRules = useMemo(
    () => siteShelf(snapshot?.shelves, experience.id),
    [snapshot, experience.id],
  );
  const { pinned, shelf } = useMemo(() => splitShelf(all, shelfRules), [all, shelfRules]);
  const filterContext = useMemo<ShelfLabels>(
    () => ({
      keywordLabels: labels.keywords,
      categoryLabels: labels.categories,
      industryLabels: labels.industries,
    }),
    [labels],
  );
  const counts = useMemo(
    () => countFacets(shelf, filters, filterContext),
    [shelf, filters, filterContext],
  );
  const categoryChoices = useMemo(
    () => categoryOptions(vocabulary?.categories ?? [], shelf, labels.categories),
    [vocabulary, shelf, labels],
  );
  const industryChoices = useMemo(
    () =>
      vocabularyOptions(
        vocabulary?.industries ?? [],
        shelf.flatMap((entry) => entry.industryKeys ?? []),
        new Map(),
      ),
    [vocabulary, shelf],
  );
  const collectionChoices = useMemo(
    () =>
      vocabularyOptions(vocabulary?.keywords ?? [], shelf.flatMap(collectionsOf), labels.keywords),
    [vocabulary, shelf, labels],
  );
  const capabilityChoices = useMemo(
    () =>
      vocabularyOptions(
        vocabulary?.capabilities ?? [],
        shelf.flatMap(capabilitiesOf),
        labels.keywords,
      ),
    [vocabulary, shelf, labels],
  );
  const newestAvailable = useMemo(() => canSortByNewest(shelf), [shelf]);
  const sort = filters.sort === "newest" && !newestAvailable ? "recommended" : filters.sort;
  const filtering = hasActiveFilters(filters);
  const shownTemplates = useMemo(() => {
    const matched = orderShelf(
      filterShelf(shelf, filters, filterContext),
      locale,
      shelfRules,
      sort,
    );
    return filtering ? matched : [...pinned, ...matched];
  }, [shelf, filters, filterContext, locale, shelfRules, sort, pinned, filtering]);
  const listedCount = shelf.length + pinned.length;
  // Server-computed tier flag + localized plan NAME — never raw plan ids.
  const currentPlan = billingQuery.data?.account.plan;
  const planBadge = currentPlan
    ? currentPlan.isFreeTier
      ? {
          label: intl.formatMessage({ id: "tpl.freePlanBadge" }),
          bg: "var(--brand-green-bg)",
          fg: "var(--brand-green-deep)",
        }
      : { label: `⭐ ${currentPlan.name}`, bg: "var(--brand-yellow)", fg: ink }
    : null;
  const showProUpsell = anonymous || currentPlan?.isFreeTier === true;
  // Signed in, a card opens the detail as the V1 modal over this gallery (the background-
  // location pattern) and as a standalone page on refresh or direct load. The public tree
  // has no overlay, so signed out the detail always renders standalone.
  const cardState = anonymous ? undefined : { background: location };

  return (
    <div className={styles.page}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: "16px", flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--brand-font-display)",
              fontWeight: 800,
              fontSize: "30px",
            }}
          >
            <FormattedMessage
              id={experience.id === "myfirstagent" ? "templates.title.mfa" : "templates.title.lya"}
            />
          </h1>
          <div
            style={{
              fontWeight: 600,
              fontSize: "15px",
              color: "var(--brand-muted-2)",
              marginTop: "4px",
            }}
          >
            <FormattedMessage id="tpl.subtitle" />
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {planBadge ? (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              padding: "6px 14px",
              border: `var(--brand-bw-2-5) solid ${ink}`,
              borderRadius: "var(--brand-r-999)",
              boxShadow: "var(--brand-shadow-2-5)",
              fontFamily: "var(--brand-font-display)",
              fontWeight: 800,
              fontSize: "12.5px",
              background: planBadge.bg,
              color: planBadge.fg,
            }}
          >
            {planBadge.label}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => navigate("/start")}
        style={{
          marginTop: "18px",
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: "18px",
          flexWrap: "wrap",
          background: "var(--brand-violet-bg)",
          border: `var(--brand-bw-2-5) solid ${ink}`,
          borderRadius: "var(--brand-r-18)",
          boxShadow: "var(--brand-shadow-4)",
          padding: "18px 22px",
          cursor: "pointer",
        }}
      >
        <div style={{ flex: 1, minWidth: "min(220px, 100%)" }}>
          <div
            style={{
              fontFamily: "var(--brand-font-display)",
              fontWeight: 800,
              fontSize: "18px",
              color: ink,
            }}
          >
            <FormattedMessage id="wizard.cta.title" />
          </div>
          <div
            style={{
              fontWeight: 600,
              fontSize: "13.5px",
              color: "var(--brand-muted)",
              marginTop: "3px",
            }}
          >
            <FormattedMessage id="wizard.cta.body" />
          </div>
        </div>
        <span
          style={{
            padding: "10px 18px",
            background: "var(--brand-violet)",
            border: `var(--brand-bw-2-5) solid ${ink}`,
            borderRadius: "var(--brand-r-12)",
            boxShadow: "var(--brand-shadow-3)",
            fontFamily: "var(--brand-font-display)",
            fontWeight: 800,
            fontSize: "13px",
            color: card,
          }}
        >
          <FormattedMessage id="wizard.cta.button" />
        </span>
      </button>

      <TemplateFilters
        filters={{ ...filters, sort }}
        counts={counts}
        categories={categoryChoices}
        industries={industryChoices}
        collections={collectionChoices}
        capabilities={capabilityChoices}
        shown={shownTemplates.length}
        total={listedCount}
        canSortByNewest={newestAvailable}
        onChange={setFilters}
        onSearch={onSearch}
      />

      {snapshotQuery.isLoading ? (
        <div
          style={{
            marginTop: "20px",
            padding: "16px 18px",
            border: "var(--brand-bw-2) dashed var(--brand-violet-line)",
            borderRadius: "var(--brand-r-14)",
            font: "700 13px var(--brand-font-body)",
            color: "var(--brand-muted-3)",
          }}
        >
          <FormattedMessage id="tpl.loading" />
        </div>
      ) : null}
      <InlineApiError error={snapshotQuery.error} />

      {snapshot && filtering && shownTemplates.length === 0 ? (
        <div
          data-testid="no-results"
          style={{
            marginTop: "20px",
            padding: "26px 22px",
            border: "var(--brand-bw-2) dashed var(--brand-violet-line)",
            borderRadius: "var(--brand-r-16)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: "var(--brand-font-display)",
              fontWeight: 800,
              fontSize: "17px",
              color: ink,
            }}
          >
            <FormattedMessage id="tpl.noResults.title" />
          </div>
          <div
            style={{
              fontWeight: 600,
              fontSize: "13.5px",
              color: "var(--brand-muted)",
              marginTop: "4px",
            }}
          >
            <FormattedMessage id="tpl.noResults.body" />
          </div>
          <button
            type="button"
            onClick={() => setFilters(clearFilters(filters))}
            style={{
              marginTop: "14px",
              padding: "8px 16px",
              background: card,
              border: `var(--brand-bw-2-5) solid ${ink}`,
              borderRadius: "var(--brand-r-999)",
              boxShadow: "var(--brand-shadow-2-5)",
              cursor: "pointer",
              fontFamily: "var(--brand-font-display)",
              fontWeight: 800,
              fontSize: "13px",
              color: ink,
            }}
          >
            <FormattedMessage id="tpl.filter.clear" />
          </button>
        </div>
      ) : null}

      {/* A card click writes a search still waiting on its debounce first, so the URL the
          detail returns to holds it. */}
      <div data-testid="template-grid" className={styles.grid} onClickCapture={flushSearch}>
        {shownTemplates.map((entry) => (
          <TemplateCard
            key={entry.id}
            entry={entry}
            to={templateDetailPath(entry.libraryKey, entry.slug)}
            state={cardState}
            siteId={experience.id}
            categoryLabels={labels.categories}
            keywordLabels={labels.keywords}
            categoryOrder={labels.categoryOrder}
          />
        ))}
      </div>

      {showProUpsell ? (
        <div
          style={{
            marginTop: "26px",
            background: "var(--brand-ink-deep)",
            border: `var(--brand-bw-2-5) solid ${ink}`,
            borderRadius: "var(--brand-r-20)",
            boxShadow: "var(--brand-shadow-5)",
            padding: "26px clamp(18px, 4vw, 30px)",
            display: "flex",
            alignItems: "center",
            gap: "22px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: "min(240px, 100%)" }}>
            <div
              style={{
                fontFamily: "var(--brand-font-display)",
                fontWeight: 800,
                fontSize: "21px",
                color: card,
              }}
            >
              <FormattedMessage id="tpl.upsellTitle" />
            </div>
            <div
              style={{
                fontWeight: 600,
                fontSize: "14px",
                color: "var(--brand-lavender-soft)",
                marginTop: "5px",
              }}
            >
              <FormattedMessage id="tpl.upsellBody" />
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(anonymous ? "/pricing" : "/billing")}
            style={{
              padding: "12px 24px",
              background: "var(--brand-yellow)",
              border: `var(--brand-bw-2-5) solid ${ink}`,
              borderRadius: "var(--brand-r-14)",
              boxShadow: "var(--brand-shadow-4)",
              cursor: "pointer",
              fontFamily: "var(--brand-font-display)",
              fontWeight: 800,
              fontSize: "15px",
              color: ink,
            }}
          >
            <FormattedMessage id="tpl.goPro" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
